import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { placementAPI } from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import {
  ArrowLeft, LogOut, Users, Briefcase,
  IndianRupee, Clock, Star, Loader2, CheckCircle
} from 'lucide-react';

const STATUS_CONFIG = {
  ACTIVE:     { label: 'Active',     color: 'bg-green-100 text-green-700'  },
  COMPLETED:  { label: 'Completed',  color: 'bg-blue-100 text-blue-700'    },
  TERMINATED: { label: 'Terminated', color: 'bg-red-100 text-red-600'      },
};

function StarRating({ value, onChange, readOnly }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button"
          onClick={() => !readOnly && onChange && onChange(n)}
          className={`transition ${readOnly ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}>
          <Star className={`h-5 w-5 ${n <= value ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
        </button>
      ))}
    </div>
  );
}

export default function CompanyPlacements() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [releaseModal, setReleaseModal] = useState(null);
  const [form, setForm] = useState({ actualEndDate: '', rating: 0, ratingText: '', releaseNote: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await placementAPI.companyPlacements();
      setPlacements(res.data.data || []);
    } catch { toast.error('Failed to load placements'); }
    finally { setLoading(false); }
  };

  const handleRelease = async () => {
    if (!form.rating) { toast.error('Rating is required to release staff'); return; }
    if (!form.actualEndDate) { toast.error('End date is required'); return; }
    setSubmitting(true);
    try {
      await placementAPI.releaseStaff(releaseModal, {
        actualEndDate: form.actualEndDate,
        rating: form.rating,
        ratingText: form.ratingText || null,
        releaseNote: form.releaseNote || null,
      });
      toast.success('Staff released successfully!');
      setReleaseModal(null);
      setForm({ actualEndDate: '', rating: 0, ratingText: '', releaseNote: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to release'); }
    finally { setSubmitting(false); }
  };

  const active = placements.filter(p => p.status === 'ACTIVE');
  const past   = placements.filter(p => p.status !== 'ACTIVE');

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/company/dashboard')}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <img src="/logo-horizontal.png" alt="ShramSena" className="h-7 w-auto" />
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-bold uppercase">Company</span>
          </div>
          <button onClick={() => { logout(); navigate('/company/login'); }}
            className="flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-700">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-slate-800">My Placements</h1>
          <p className="text-sm text-gray-400 mt-0.5">Active and past staff engagements</p>
        </div>

        {placements.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-2xl border border-gray-100 shadow-sm">
            <Users className="h-14 w-14 text-gray-200 mx-auto mb-4" />
            <p className="text-gray-400 font-semibold">No placements yet</p>
          </div>
        ) : (
          <div className="space-y-6">
            {active.length > 0 && (
              <div>
                <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Active</h2>
                {active.map(p => (
                  <PlacementCard key={p.id} placement={p}
                    onRelease={() => {
                      setReleaseModal(p.id);
                      setForm({ actualEndDate: '', rating: 0, ratingText: '', releaseNote: '' });
                    }} />
                ))}
              </div>
            )}
            {past.length > 0 && (
              <div>
                <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Past</h2>
                {past.map(p => <PlacementCard key={p.id} placement={p} />)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Release modal */}
      {releaseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4 overflow-y-auto py-8">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full my-auto">
            <h3 className="font-bold text-slate-800 mb-1">Release Staff</h3>
            <p className="text-sm text-gray-400 mb-5">
              Rating is mandatory. The staff member will become available again after release.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Actual End Date *</label>
                <input type="date" value={form.actualEndDate}
                  onChange={e => setForm(f => ({ ...f, actualEndDate: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500" />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-2">Rate this staff member *</label>
                <StarRating value={form.rating} onChange={r => setForm(f => ({ ...f, rating: r }))} />
                {!form.rating && <p className="text-xs text-red-500 mt-1">Rating is required</p>}
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Review <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea value={form.ratingText}
                  onChange={e => setForm(f => ({ ...f, ratingText: e.target.value }))}
                  placeholder="Share your experience working with this staff member..."
                  rows={2}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none" />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Release Note <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea value={form.releaseNote}
                  onChange={e => setForm(f => ({ ...f, releaseNote: e.target.value }))}
                  placeholder="e.g. Contract completed, excellent performance..."
                  rows={2}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none" />
              </div>
            </div>

            <div className="flex gap-3 mt-5">
              <button onClick={() => setReleaseModal(null)}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl font-semibold text-sm text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleRelease} disabled={submitting || !form.rating}
                className="flex-1 py-2.5 bg-teal-600 text-white rounded-xl font-bold text-sm hover:bg-teal-700 disabled:opacity-60">
                {submitting ? 'Releasing...' : 'Release Staff'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PlacementCard({ placement: p, onRelease }) {
  const cfg = STATUS_CONFIG[p.status] || STATUS_CONFIG.ACTIVE;
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-4">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600
            rounded-xl flex items-center justify-center text-white font-black shrink-0">
            {p.staffFirstName?.charAt(0)}{p.staffLastName?.charAt(0)}
          </div>
          <div>
            <h3 className="font-bold text-slate-800">{p.staffFirstName} {p.staffLastName}</h3>
            {p.staffTypeName && <p className="text-xs text-gray-500 mt-0.5">{p.staffTypeName}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.color}`}>
            {cfg.label}
          </span>
          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-lg font-semibold">
            {p.source === 'OPPORTUNITY' ? 'Via Opportunity' : 'Direct Hire'}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        {p.roleName && (
          <div className="bg-gray-50 rounded-xl p-3">
            <p className="text-xs text-gray-400 mb-0.5">Role</p>
            <p className="font-bold text-slate-800 text-sm">{p.roleName}</p>
          </div>
        )}
        {p.dailyRateAgreed && (
          <div className="bg-gray-50 rounded-xl p-3">
            <p className="text-xs text-gray-400 mb-0.5">Daily Rate</p>
            <p className="font-bold text-slate-800">₹{p.dailyRateAgreed}</p>
          </div>
        )}
        {p.startDate && (
          <div className="bg-gray-50 rounded-xl p-3">
            <p className="text-xs text-gray-400 mb-0.5">Start Date</p>
            <p className="font-bold text-slate-800 text-sm">
              {new Date(p.startDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            </p>
          </div>
        )}
      </div>

      {/* Staff rating of company */}
      {p.ratingByStaff && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 mb-3">
          <p className="text-xs font-bold text-blue-800 mb-1">Staff rated your company</p>
          <div className="flex items-center gap-2">
            <div className="flex gap-0.5">
              {[1,2,3,4,5].map(n => (
                <Star key={n} className={`h-4 w-4 ${n <= p.ratingByStaff ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
              ))}
            </div>
            {p.ratingByStaffText && <p className="text-xs text-blue-700">"{p.ratingByStaffText}"</p>}
          </div>
        </div>
      )}

      {/* Company's own rating */}
      {p.ratingByCompany && (
        <div className="flex items-center gap-2 text-xs text-green-600 mb-3">
          <CheckCircle className="h-4 w-4" />
          You rated this staff {p.ratingByCompany}/5
        </div>
      )}

      {p.status === 'ACTIVE' && onRelease && (
        <button onClick={onRelease}
          className="w-full mt-2 py-2.5 border border-teal-200 text-teal-600 rounded-xl
            font-bold text-sm hover:bg-teal-50 transition">
          Release Staff
        </button>
      )}
    </div>
  );
}
